import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import useFormSecurity from '../../../hooks/useFormSecurity';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiShield, FiLock, FiMail, FiArrowRight, FiAlertCircle, FiArrowLeft } = FiIcons;

const SuperAdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { authenticateUser } = useData();
  const navigate = useNavigate();
  const { HoneypotField, validateSubmission } = useFormSecurity('superadmin_login');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateSubmission()) return;
    
    setLoading(true);
    setError('');

    try {
      const authUser = await authenticateUser(email, password);
      if (authUser && authUser.role === 'superadmin') {
        const success = await login(authUser, 'superadmin');
        if (success) navigate('/superadmin');
      } else {
        setError('Ugyldig e-post eller passord for administrator.');
      }
    } catch (err) {
      setError('En feil oppstod. Vennligst prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-4 relative">
        {/* TILBAKE KNAPP */}
        <button onClick={() => navigate('/')} className="absolute top-6 left-6 text-slate-400 hover:text-white flex items-center gap-2 transition-colors font-medium">
            <SafeIcon icon={FiArrowLeft} /> Tilbake til forsiden
        </button>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-[32px] p-10 shadow-2xl">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/20">
            <SafeIcon icon={FiShield} className="text-white text-3xl" />
          </div>
          <h1 className="text-2xl font-black text-white">Intern Portal</h1>
          <p className="text-slate-400 text-sm mt-2">Kun for autorisert personell</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <HoneypotField />
          {error && <div className="bg-red-900/30 border border-red-500/50 text-red-200 p-4 rounded-xl text-xs font-bold">{error}</div>}
          
          <div>
            <label className="text-[10px] font-black uppercase text-slate-500 ml-1 mb-1 block">E-post</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-4 top-3.5 text-slate-500" />
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full pl-12 pr-4 py-3.5 bg-slate-800 border-none rounded-2xl text-white outline-none focus:ring-2 focus:ring-blue-500" placeholder="admin@hmsvarsling.no" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase text-slate-500 ml-1 mb-1 block">Passord</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-4 top-3.5 text-slate-500" />
              <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full pl-12 pr-4 py-3.5 bg-slate-800 border-none rounded-2xl text-white outline-none focus:ring-2 focus:ring-blue-500" placeholder="••••••••" />
            </div>
          </div>

          <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20">
            {loading ? 'Verifiserer...' : 'Logg inn'} <SafeIcon icon={FiArrowRight} />
          </button>
        </form>
        
        {/* DEMO KNAPP ER FJERNET HERFRA */}
      </motion.div>
    </div>
  );
};

export default SuperAdminLogin;